import type { CalendarEvent } from '../../../types/calendar';
import { TIMELINE_CONTENT_RIGHT_PAD, TIMELINE_GUTTER_WIDTH, timeToMinutes } from '../../../lib/timeline';
import type { ColumnLayout } from '../../../lib/timeline';

interface TimelineMeetingBlockProps {
  event: CalendarEvent;
  toPixel: (minutes: number) => number;
  durationToPx: (duration: number) => number;
  columnLayout?: ColumnLayout;
}

export function TimelineMeetingBlock({ event, toPixel, durationToPx, columnLayout }: TimelineMeetingBlockProps) {
  const startMin = timeToMinutes(event.startTime);
  const endMin = timeToMinutes(event.endTime);
  const duration = endMin - startMin;
  if (duration <= 0) return null;

  const top = toPixel(startMin);
  const height = Math.max(durationToPx(duration), 22);
  const isShort = height < 34;

  const col = columnLayout?.column ?? 0;
  const totalCols = columnLayout?.totalColumns ?? 1;

  // For multi-column, use percentage-based positioning
  const posStyle: React.CSSProperties = {
    top,
    height,
    position: 'absolute',
    ...(totalCols > 1
      ? {
          left: `calc(${TIMELINE_GUTTER_WIDTH}px + ${(col / totalCols) * 100}% - ${(col / totalCols) * TIMELINE_GUTTER_WIDTH}px)`,
          width: `calc(${100 / totalCols}% - ${TIMELINE_GUTTER_WIDTH / totalCols}px - ${TIMELINE_CONTENT_RIGHT_PAD / totalCols}px)`,
        }
      : {
          left: TIMELINE_GUTTER_WIDTH,
          right: TIMELINE_CONTENT_RIGHT_PAD,
        }),
  };

  return (
    <div
      className={`rounded-md overflow-hidden select-none shadow-sm
        ${event.isCancelled
          ? 'bg-bg-raised/40 opacity-50 border border-border-subtle/30'
          : 'bg-accent/8 border border-accent/20'
        }`}
      style={posStyle}
      title={`${event.title} (${event.startTime} – ${event.endTime})`}
    >
      <div className={`px-2 ${isShort ? 'py-0.5' : 'py-1'}`}>
        <span className={`text-[11px] leading-tight font-medium truncate block ${event.isCancelled ? 'line-through text-text-muted/40' : 'text-text-primary'}`}>
          {event.title}
        </span>
        {!isShort && (
          <span className="text-[10px] text-text-muted font-mono">
            {event.startTime} – {event.endTime}
          </span>
        )}
      </div>
    </div>
  );
}
